import React, { useEffect, useState } from "react";
import {
    getEmployees,
    getLedger,
    addLedgerEntry,
    updateLedgerEntry,
    deleteLedgerEntry
} from "../api/ledgerApi";

export default function AccountLedger() {
    const [accountType, setAccountType] = useState("Employee");
    const [selectedAccount, setSelectedAccount] = useState("");

    const [employees, setEmployees] = useState([]);
    const [ledgerData, setLedgerData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const customers = [
        { id: 101, name: "Mohan Traders" },
        { id: 102, name: "Rakesh Food Corner" }
    ];

    const suppliers = [
        { id: 201, name: "Fresh Vegetable Supplier" },
        { id: 202, name: "Dairy Distributor" }
    ];

    useEffect(() => {
        loadEmployees();
    }, []);

    const loadEmployees = async () => {
        try {
            const data = await getEmployees();
            setEmployees(data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const searchLedger = async () => {
        // Account validation
        if (!accountType) {
            alert("Please select account type");
            return;
        }

        if (!selectedAccount) {
            alert("Please select account");
            return;
        }

        // Date validation
        if (!fromDate) {
            alert("Please select From Date");
            return;
        }

        if (!toDate) {
            alert("Please select To Date");
            return;
        }

        // Date range validation
        if (new Date(fromDate) > new Date(toDate)) {
            alert("From Date cannot be greater than To Date");
            return;
        }

        try {
            setLoading(true);

            const response = await getLedger({
                accountType: accountType.toLowerCase(),
                accountId: selectedAccount,
                fromDate,
                toDate
            });

            setLedgerData(response?.data || []);
        } catch (error) {
            console.error(error);
            alert("Failed to load ledger data");
        } finally {
            setLoading(false);
        }
    };

    const handleAddLedger = async () => {
        try {
            await addLedgerEntry({
                accountType,
                accountId: selectedAccount,
                transactionDate: new Date(),
                description: "New Entry",
                credit: 1000,
                debit: 0,
                remarks: "Manual Entry"
            });

            searchLedger();
        } catch (error) {
            console.error(error);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteLedgerEntry(id);

            setLedgerData((prev) =>
                prev.filter((item) => item.id !== id)
            );
        } catch (error) {
            console.error(error);
        }
    };

    const handleEdit = async (row) => {
        try {
            await updateLedgerEntry(row.id, row);

            searchLedger();
        } catch (error) {
            console.error(error);
        }
    };

    const getAccountList = () => {
        switch (accountType) {
            case "Employee":
                return employees;

            case "Customer":
                return customers;

            case "Supplier":
                return suppliers;

            default:
                return [];
        }
    };

    const totalCredit = ledgerData.reduce(
        (sum, item) => sum + Number(item.credit || 0),
        0
    );

    const totalDebit = ledgerData.reduce(
        (sum, item) => sum + Number(item.debit || 0),
        0
    );

    const currentBalance = totalCredit - totalDebit;

    return (
        <div className="p-6 bg-[var(--app-bg)] text-[var(--text)] min-h-screen">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-[var(--text)]">
                        Account Ledger Management
                    </h1>

                    <p className="text-[var(--text-muted)] mt-1">
                        Employee • Customer • Supplier Khata
                    </p>
                </div>

                <button
                    onClick={handleAddLedger}
                    className="mt-4 md:mt-0 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-xl font-medium shadow-lg"
                >
                    + Add New Entry
                </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-[var(--surface-alt)] rounded-2xl shadow p-5 border border-[var(--border)] border-l-4 border-green-500">
                    <p className="text-[var(--text-muted)] text-sm">
                        Total Credit
                    </p>

                    <h2 className="text-2xl font-bold text-green-600">
                        ₹{totalCredit}
                    </h2>
                </div>

                <div className="bg-[var(--surface-alt)] rounded-2xl shadow p-5 border border-[var(--border)] border-l-4 border-red-500">
                    <p className="text-[var(--text-muted)] text-sm">
                        Total Debit
                    </p>

                    <h2 className="text-2xl font-bold text-red-600">
                        ₹{totalDebit}
                    </h2>
                </div>

                <div className="bg-[var(--surface-alt)] rounded-2xl shadow p-5 border border-[var(--border)] border-l-4 border-blue-500">
                    <p className="text-[var(--text-muted)] text-sm">
                        Current Balance
                    </p>

                    <h2 className="text-2xl font-bold text-blue-600">
                        ₹{currentBalance}
                    </h2>
                </div>

                <div className="bg-[var(--surface-alt)] rounded-2xl shadow p-5 border border-[var(--border)] border-l-4 border-orange-500">
                    <p className="text-[var(--text-muted)] text-sm">
                        Records
                    </p>

                    <h2 className="text-2xl font-bold text-orange-500">
                        {ledgerData.length}
                    </h2>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-[var(--surface-alt)] rounded-2xl border border-[var(--border)] shadow mb-6">
                <div className="border-b border-[var(--border)] px-6 py-4">
                    <h3 className="font-semibold text-lg">
                        Search Filters
                    </h3>
                </div>

                <div className="p-6 grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                            Account Type
                        </label>

                        <select
                            value={accountType}
                            onChange={(e) => {
                                setAccountType(e.target.value);
                                setSelectedAccount("");
                            }}
                            className="w-full border border-[var(--border)] rounded-xl bg-[var(--input-bg)] text-[var(--text)] px-4 py-2"
                        >
                            <option value="Employee">Employee</option>
                            <option value="customer">Customer</option>
                            <option value="supplier">Supplier</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                            {accountType} Name
                        </label>

                        <select
                            value={selectedAccount}
                            onChange={(e) =>
                                setSelectedAccount(
                                    e.target.value
                                )
                            }
                            className="w-full border border-[var(--border)] rounded-xl bg-[var(--input-bg)] text-[var(--text)] px-4 py-2"
                        >
                            <option value="">
                                Select {accountType}
                            </option>

                            {getAccountList().map(
                                (account) => (
                                    <option
                                        key={account.id}
                                        value={account.uid}
                                    >
                                        {account.name}
                                    </option>
                                )
                            )}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                            From Date
                        </label>

                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) =>
                                setFromDate(
                                    e.target.value
                                )
                            }
                            className="w-full border border-[var(--border)] rounded-xl bg-[var(--input-bg)] text-[var(--text)] px-4 py-2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                            To Date
                        </label>

                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) =>
                                setToDate(
                                    e.target.value
                                )
                            }
                            className="w-full border border-[var(--border)] rounded-xl bg-[var(--input-bg)] text-[var(--text)] px-4 py-2"
                        />
                    </div>

                    <div className="flex items-end">
                        <button
                            onClick={searchLedger}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-xl font-medium"
                        >
                            {loading
                                ? "Loading..."
                                : "Search"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-[var(--surface-alt)] rounded-2xl border border-[var(--border)] shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-[var(--border)]">
                    <h3 className="text-lg font-semibold">
                        {accountType} Ledger Entries
                    </h3>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-[var(--surface-soft)]">
                            <tr>
                                <th className="text-left px-6 py-4">
                                    #
                                </th>
                                <th className="text-left px-6 py-4">
                                    Date
                                </th>
                                <th className="text-left px-6 py-4">
                                    Description
                                </th>
                                <th className="text-left px-6 py-4 text-green-600">
                                    Credit
                                </th>
                                <th className="text-left px-6 py-4 text-red-600">
                                    Debit
                                </th>
                                <th className="text-left px-6 py-4">
                                    Balance
                                </th>
                                <th className="text-left px-6 py-4">
                                    Actions
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {ledgerData.length > 0 ? (
                                ledgerData.map(
                                    (row, index) => (
                                        <tr
                                            key={row.id}
                                            className="border-t border-[var(--border)] hover:bg-[var(--surface-soft)]"
                                        >
                                            <td className="px-6 py-4">
                                                {index + 1}
                                            </td>

                                            <td className="px-6 py-4">
                                                {row.date}
                                            </td>

                                            <td className="px-6 py-4">
                                                {
                                                    row.description
                                                }
                                            </td>

                                            <td className="px-6 py-4 text-green-600 font-semibold">
                                                ₹
                                                {
                                                    row.credit
                                                }
                                            </td>

                                            <td className="px-6 py-4 text-red-600 font-semibold">
                                                ₹
                                                {row.debit}
                                            </td>

                                            <td className="px-6 py-4 font-semibold">
                                                ₹
                                                {
                                                    row.balance
                                                }
                                            </td>

                                            <td className="px-6 py-4">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() =>
                                                            handleEdit(
                                                                row
                                                            )
                                                        }
                                                        className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1 rounded-lg text-sm"
                                                    >
                                                        Edit
                                                    </button>

                                                    <button
                                                        onClick={() =>
                                                            handleDelete(
                                                                row.id
                                                            )
                                                        }
                                                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg text-sm"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                )
                            ) : (
                                <tr>
                                    <td
                                        colSpan="7"
                                        className="text-center py-6 text-[var(--text-muted)]"
                                    >
                                        No ledger records found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}